import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Clock } from "lucide-react";
import { api } from "@/lib/auth";
import { SubpageHeader } from "@/components/SubpageHeader";
import { MaskedLines, EASE } from "@/components/Reveal";
import { COMPANY } from "@/data/content";

export const JOB_BRAND = {
  artwork360: { label: "Artwork 360", color: "#5271FF" },
  medlyn360: { label: "Medlyn 360", color: "#4FC3E8" },
  malansh: { label: "Malansh Solutions", color: "#2A3192" },
};
const FILTERS = ["all", "medlyn360", "artwork360", "malansh"];

export default function Careers() {
  const [jobs, setJobs] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
  }, []);

  const shown = useMemo(() => (jobs || []).filter((j) => filter === "all" || j.brand === filter), [jobs, filter]);

  return (
    <div data-testid="careers-page" className="min-h-screen bg-brand-ice">
      <SubpageHeader testId="careers-header" />
      <section className="bg-brand-navy px-6 pb-20 pt-16 text-white lg:pt-24">
        <div className="mx-auto max-w-[1200px]">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-cyan">Careers · Mumbai</div>
          <MaskedLines as="h1" delay={0.1} className="mt-5 font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl" lines={["Do work that matters", "for clients across the US."]} />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.6, ease: EASE }} className="mt-6 max-w-2xl text-base text-white/60 md:text-lg">
            Malansh Solutions builds and runs remote teams for US healthcare practices and creative businesses. We hire carefully, train properly and give you real ownership from day one.
          </motion.p>
        </div>
      </section>

      <main className="mx-auto max-w-[1200px] px-6 py-14">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <h2 className="font-display text-2xl font-bold tracking-tight text-brand-ink sm:text-3xl">Open roles <span className="ml-2 font-mono text-base text-brand-indigo/60">{jobs ? shown.length : ""}</span></h2>
          <div data-testid="careers-filters" className="flex flex-wrap gap-2 rounded-full border border-brand-indigo/10 bg-white p-1">
            {FILTERS.map((f) => (
              <button key={f} data-testid={`careers-filter-${f}`} onClick={() => setFilter(f)} className={`rounded-full px-4 py-2 text-xs font-semibold transition ${filter === f ? "bg-brand-indigo text-white" : "text-brand-ink/60 hover:text-brand-ink"}`}>
                {f === "all" ? "All teams" : JOB_BRAND[f].label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-4">
          {jobs === null && <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand-indigo/60">Loading roles…</div>}
          {jobs && shown.length === 0 && (
            <div data-testid="careers-empty" className="rounded-3xl border border-dashed border-brand-indigo/20 bg-white p-12 text-center">
              <p className="font-display text-lg font-semibold text-brand-ink">No open roles in this team right now.</p>
              <p className="mt-2 text-sm text-slate-500">Send your CV to <a href={`mailto:${COMPANY.email}`} className="text-brand-indigo underline">{COMPANY.email}</a> and we'll keep it on file.</p>
            </div>
          )}
          {shown.map((j, i) => (
            <motion.div key={j.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 * i, ease: EASE }}>
              <Link to={`/careers/${j.id}`} data-testid={`job-card-${j.id}`} className="group block rounded-[1.5rem] border border-brand-indigo/10 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-brand-indigo/30 hover:shadow-[0_20px_60px_-30px_rgba(42,49,146,.35)] sm:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: JOB_BRAND[j.brand].color }}><span className="h-2 w-2 rounded-full" style={{ background: JOB_BRAND[j.brand].color }} />{JOB_BRAND[j.brand].label}</div>
                    <h3 className="mt-3 font-display text-xl font-semibold tracking-tight text-brand-ink sm:text-2xl">{j.title}</h3>
                    <p className="mt-2 max-w-2xl text-sm text-slate-600 sm:text-base">{j.summary}</p>
                    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500 sm:text-sm">
                      <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{j.employment_type}</span>
                      <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{j.location}</span>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-2 font-display text-sm font-semibold text-brand-indigo">View & apply <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" /></span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
